<template>
    <div class="trends-chart">
        <div class="trends-chart__canvas-wrapper" :style="chartCanvasWrapperStyle">
            <canvas ref="chartRef" />
        </div>
    </div>
</template>
<script setup lang="ts">
    import {computed, onMounted, onUnmounted, shallowRef, useTemplateRef} from 'vue';
    import type {BarElement, BarProps, ChartConfiguration, ChartDataset, ScriptableContext} from 'chart.js';
    import {Chart} from 'chart.js/auto';
    import type {Context as ChartDataLabelsContext} from 'chartjs-plugin-datalabels';
    import skillsGrowingData from '~/assets/data/main-supply-top-growing-skills.json';
    import skillsVolumeData from '~/assets/data/main-supply-top-skills-volume.json';
    import type {TrendsSkills} from '~/types/TrendsData';
    import {barLeftCircleLabelsPlugin} from '~/utils/chartjsBarLeftCircleLabels';
    import {barRightLabelsPlugin} from '~/utils/chartjsBarRightLabels';
    import {referenceLinesPlugin} from '~/utils/chartjsReferenceLines';

    const CHART_MIN_PX_PER_CATEGORY = 44;

    function barBorderRadiusByThickness(ctx: ScriptableContext<'bar'>): number {
        const meta = ctx.chart.getDatasetMeta(ctx.datasetIndex);
        const el = meta.data[ctx.dataIndex] as BarElement & BarProps;
        const h = el?.height ?? 0;
        if (h <= 0) {
            return 25;
        }
        return Math.min(25, h / 2);
    }

    enum DataType {
        SKILLS_VOLUME = 'main-supply-top-skills-volume',
        SKILLS_GROWING = 'main-supply-top-growing-skills',
    }

    const props = defineProps<{
        data: string;
    }>();

    const {datasets, labels} = useChartData(props.data);
    const {chartCanvasWrapperStyle} = useChartJs(datasets, labels);

    function useChartData(_data: string) {
        function categoryData(): TrendsSkills[] {
            const isVolume = _data === DataType.SKILLS_VOLUME;
            const items = (isVolume ? skillsVolumeData : skillsGrowingData) as TrendsSkills[];

            return [...items].sort((a, b) => (isVolume ? b.part! - a.part! : b.growth - a.growth));
        }

        return {
            // a-z
            // a-z
            labels: categoryData().map((item: TrendsSkills) => item.display_name),
            datasets: [
                {
                    axis: 'y',
                    label: '',
                    data: categoryData().map((item: TrendsSkills) => (_data === DataType.SKILLS_VOLUME ? item.part : item.growth)),
                    growthValues: categoryData().map((item: TrendsSkills) => item.growth),
                    fill: true,
                    borderRadius: barBorderRadiusByThickness,
                } as ChartDataset<'bar', number[]> & {growthValues: number[]},
            ],
        };
    }

    function useChartJs(datasets: ChartDataset<'bar', number[]>[], labels: string[]) {
        const chart = shallowRef<Chart<'bar'> | null>(null);
        const chartRef = useTemplateRef<HTMLCanvasElement>('chartRef');

        const chartCanvasWrapperStyle = computed(() => ({
            minHeight: `${Math.max(labels.length * CHART_MIN_PX_PER_CATEGORY, 240)}px`,
        }));

        const chartConfig: ChartConfiguration<'bar'> = {
            type: 'bar',
            plugins: [barLeftCircleLabelsPlugin, barRightLabelsPlugin],
            data: {
                labels,
                datasets,
            },
            options: {
                indexAxis: 'y',
                responsive: true,
                maintainAspectRatio: false,
                interaction: {
                    mode: 'nearest',
                    intersect: true,
                    axis: 'y',
                },
                backgroundColor: '#FF91F0',
                layout: {
                    padding: {
                        left: -20,
                        right: 50,
                    },
                },
                scales: {
                    x: {
                        type: 'logarithmic',
                        ticks: {
                            display: false,
                        },
                        border: {
                            display: false,
                        },
                    },
                    y: {
                        ticks: {
                            color: '#4B4B4B',
                            padding: 25,
                            font: {
                                size: 13,
                                weight: 'bold',
                                family: 'Moderat',
                            },
                        },
                        grid: {
                            display: false,
                        },
                    },
                },
                plugins: {
                    tooltip: {
                        enabled: false,
                    },
                    legend: {
                        display: false,
                    },
                    barLeftCircleLabels: {
                        overlapOffset: 25,
                        gap: 6,
                        minRadius: 16,
                        textPadding: 6,
                        backgroundColor: 'var(--joy-color-neutral-0)',
                        borderColor: '#FF91F0',
                        borderWidth: 2,
                        color: '#EB73DE',
                        font: {
                            size: 13,
                            weight: 'bold',
                            family: 'Moderat',
                        },
                        formatter(value: number, _context: ChartDataLabelsContext): string | null {
                            return `${value}%`;
                        },
                    },
                    barRightLabels: {
                        gap: 8,
                        color: '#EB73DE',
                        font: {
                            size: 13,
                            weight: 'bold',
                            family: 'Moderat',
                        },
                        formatter(_value: number, context: ChartDataLabelsContext): string | null {
                            const growth = (context.dataset as ChartDataset<'bar', number[]> & {growthValues: number[]}).growthValues?.[
                                context.dataIndex
                            ];
                            if (growth == null) {
                                return null;
                            }
                            return props.data === DataType.SKILLS_VOLUME ? `${growth}pt` : null;
                        },
                    },
                },
            },
        };

        onMounted(() => {
            if (!chartRef.value) {
                return;
            }
            Chart.register(referenceLinesPlugin);
            const instance = new Chart(chartRef.value, chartConfig);
            chart.value = instance;
        });

        onUnmounted(() => {
            chart.value?.destroy();
            chart.value = null;
        });

        return {
            // a-z functions
            // a-z
            chart,
            chartCanvasWrapperStyle,
            chartConfig,
            chartRef,
        };
    }
</script>
<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .trends-chart {
        &__filter-bar {
            margin: var(--joy-core-spacing-4) 0;

            .joy-checkbox .joy-checkbox__content-wrapper {
                align-items: center;

                .trends-chart__category-dot {
                    margin-right: var(--joy-core-spacing-1);
                }
            }

            @include mq.screen_md(min) {
                display: none;
            }

            > * {
                flex: 1;
            }
        }
        &__filters {
            display: none;
            @include mq.screen_md(min) {
                display: flex;
                flex-wrap: wrap;
                align-items: center;
                justify-content: space-between;
                gap: 22px;
                margin: 0 var(--joy-core-spacing-4);
            }
        }
        &__category-dot {
            width: 8px;
            height: 8px;
            border-radius: 50%;
            aspect-ratio: 1 / 1;
        }
        &__canvas-wrapper {
            position: relative;
            width: 100%;
            max-height: calc(100vh - 250px);

            @include mq.screen_md(min) {
                margin-bottom: var(--joy-core-spacing-8);
            }
        }
    }
</style>
